import React, { useRef, useState } from 'react';
import { Bold, Italic, Underline, List, ListOrdered, Link2 } from 'lucide-react';

/**
 * Lightweight contentEditable rich-text composer.
 * Uses execCommand for B/I/U/lists/links. No external dep.
 * Returns HTML via onChange.
 */
export const RichTextEditor = ({ value, onChange, placeholder = 'Write your message…', minHeight = 180 }) => {
  const ref = useRef(null);
  const [, force] = useState(0);

  // Keep DOM in sync ONLY when the value prop changes externally
  // (e.g. when "Reply" prefills the body). Avoids fighting the cursor
  // while the user is actively typing.
  React.useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || '')) {
      ref.current.innerHTML = value || '';
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const cmd = (c, arg = null) => {
    ref.current?.focus();
    document.execCommand(c, false, arg);
    onChange?.(ref.current?.innerHTML || '');
    force((n) => n + 1);
  };

  const handleInput = () => onChange?.(ref.current?.innerHTML || '');

  const addLink = () => {
    const url = window.prompt('Link URL', 'https://');
    if (url) cmd('createLink', url);
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid="ai-email-richtext">
      <div className="flex items-center gap-0.5 border-b border-slate-100 px-2 py-1.5">
        <button data-testid="rt-bold" type="button" onClick={() => cmd('bold')} className="rounded p-1 text-slate-600 hover:bg-slate-100"><Bold className="h-3.5 w-3.5" /></button>
        <button data-testid="rt-italic" type="button" onClick={() => cmd('italic')} className="rounded p-1 text-slate-600 hover:bg-slate-100"><Italic className="h-3.5 w-3.5" /></button>
        <button data-testid="rt-underline" type="button" onClick={() => cmd('underline')} className="rounded p-1 text-slate-600 hover:bg-slate-100"><Underline className="h-3.5 w-3.5" /></button>
        <div className="mx-1 h-4 w-px bg-slate-200" />
        <button data-testid="rt-ul" type="button" onClick={() => cmd('insertUnorderedList')} className="rounded p-1 text-slate-600 hover:bg-slate-100"><List className="h-3.5 w-3.5" /></button>
        <button data-testid="rt-ol" type="button" onClick={() => cmd('insertOrderedList')} className="rounded p-1 text-slate-600 hover:bg-slate-100"><ListOrdered className="h-3.5 w-3.5" /></button>
        <div className="mx-1 h-4 w-px bg-slate-200" />
        <button data-testid="rt-link" type="button" onClick={addLink} className="rounded p-1 text-slate-600 hover:bg-slate-100"><Link2 className="h-3.5 w-3.5" /></button>
      </div>
      <div
        ref={ref}
        data-testid="ai-email-richtext-body"
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        className="prose prose-sm max-w-none px-3 py-2 text-sm outline-none"
        style={{ minHeight }}
        data-placeholder={placeholder}
      />
      <style>{`
        [contenteditable=true]:empty:before {
          content: attr(data-placeholder);
          color: #94a3b8;
          pointer-events: none;
        }
      `}</style>
    </div>
  );
};
